import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { RotatingLines } from "react-loader-spinner";

const RecurrenceScopeModal = ({
  open,
  title,
  message,
  options,
  defaultValue,
  confirmLabel,
  danger,
  loading,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslate();
  const [selectedValue, setSelectedValue] = useState(defaultValue);

  useEffect(() => {
    if (open) setSelectedValue(defaultValue);
  }, [open, defaultValue]);

  if (!open) return null;

  const handleOverlayClick = () => {
    if (!loading) onCancel();
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <TextGroup>
          <ModalTitle>{title}</ModalTitle>
          <ModalMessage>{message}</ModalMessage>
        </TextGroup>

        <OptionsList>
          {options.map((option) => (
            <OptionCard
              key={option.value}
              $active={selectedValue === option.value}
              $disabled={option.disabled || loading}
              onClick={() => !option.disabled && !loading && setSelectedValue(option.value)}
            >
              <RadioOuter $active={selectedValue === option.value} $disabled={option.disabled || loading}>
                {selectedValue === option.value && <RadioDot />}
              </RadioOuter>
              <OptionTextGroup>
                <OptionLabel>{option.label}</OptionLabel>
                <OptionDescription>{option.description}</OptionDescription>
              </OptionTextGroup>
            </OptionCard>
          ))}
        </OptionsList>

        <ButtonRow>
          <GhostButton onClick={onCancel} disabled={loading}>
            {t("general.cancel")}
          </GhostButton>
          <ConfirmButton $danger={danger} disabled={loading} onClick={() => !loading && onConfirm(selectedValue)}>
            {loading ? <RotatingLines strokeColor="currentColor" strokeWidth="4" animationDuration="0.75" width="18" visible={true} /> : confirmLabel}
          </ConfirmButton>
        </ButtonRow>
      </ModalCard>
    </ModalOverlay>
  );
};

export default RecurrenceScopeModal;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 460px;
  padding: 28px;
  background: ${({ theme }) => theme.menu.background};
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 12px;
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ModalTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const ModalMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.menu.selectedTextColor : "transparent")};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

const RadioOuter = styled.div`
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border-radius: 50%;
  border: 1.6px solid ${({ theme, $active, $disabled }) => ($active && !$disabled ? theme.menu.selectedTextColor : "#5A5D68")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RadioDot = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ theme }) => theme.menu.selectedTextColor};
`;

const OptionTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OptionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const OptionDescription = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
`;

const GhostButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  border-radius: 4px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfirmButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 4px;
  background: ${({ theme, $danger }) => ($danger ? "rgba(238, 80, 80, 0.1)" : theme.primaryButton.background)};
  border: 1px solid ${({ theme, $danger }) => ($danger ? theme.menu.deleteColor : theme.primaryButton.borderColor)};
  color: ${({ theme, $danger }) => ($danger ? theme.menu.deleteColor : theme.primaryButton.color)};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;
